import * as L from "leaflet";
import { MAP_CONSTANTS } from "@/common/map.constants.js";
import { TASK_CONSTANTS } from "@/common/task.constants.js";

import { loggingService } from "./logging.service";

import { dateService } from "./date.service";

export const mapMarkerService = {

  LOADING_CONTENT: 'Loading...',

  /**
   * Returns an unsorted list of task features from the list of features
   * @param {*} features
   * @returns
   */
  getUnsortedTasks,

  getAlternateAddresses,

  /**Feature sorting methods */

  /**
   * Sorts an array of features into feature arrays based on the feature's properties tag field
   * value. These are all task based features: staff home, alt addr, tasks, valid tasks, suspect
   * tasks, EROC Tasks, MROC tasks and polys
   * @param {*} features 
   * @returns 
   */
  sortTaskRelatedFeatures,

  /**
   * Sorts an array of features into feature arrays based on the feature's properties tag field
   * value. These are all cluster based features: cluster and stop point
   * @param {*} features 
   * @returns 
   */
  sortClusterFeatures,

    /**
     * Sorts an array of features into feature arrays based on the feature's properties tag field
     * value. These are all track based features (phone or laptop): gps point, closest gps point
     * and travel lines
     * @param {*} features 
     * @param {*} allTasks
     * @returns 
     */
  sortDayTrackMarkers,

  sortAssignmentMarkers,
    
  /**
   * Gets map markers for staff home address
   * @param {*} features
   * @param {*} fnPopup
   */
  getHomeAddressMarkers,

  /**
   * Gets map markers for no data tasks
   * @param {*} features
   * @param {*} fnPopup
   */
  getNoDataTaskMarkers,

  /**
   * Gets map markers for valid tasks
   * @param {*} features
   * @param {*} fnPopup
   */
  getValidTaskMarkers,

  /**
   * Gets map markers for valid tasks in alternate location
   * @param {*} features
   * @param {*} fnPopup
   */
  getValidTaskAlternativeLocationMarkers,

  /**
   * Gets map markers for EROC tasks
   * @param {*} features
   * @param {*} fnPopup
   */
  getErocTaskMarkers,

  /**
   * Gets map markers for MROC tasks
   * @param {*} features
   * @param {*} fnPopup
   */
  getMrocTaskMarkers,

  /**
   * Gets map markers for generic tasks
   * @param {*} features
   * @param {*} fnPopup
   */
  getTaskMarkers,

  /**
   * Gets map markers for suspect tasks
   * @param {*} features
   * @param {*} fnPopup
   */
  getSuspectTaskMarkers,

  /**
   * Gets map markers for polylines
   * @param {*} features
   * @param {*} fnPopup
   */
  getPolyMarkers,

  /**
   * Gets all other markers
   */
  getOtherMarkers,
  
  /**
   * Gets map markers for clusters
   * @param {*} features
   * @param {*} fnPopup
   */    
  getClusterMarkers,
  
  /**
   * Gets map markers for stop points
   * @param {*} features
   * @param {*} fnPopup
   */
  getStopPointMarkers,

  /**
   * Gets map markers for laptop gps points
   * @param {*} features
   */
  getLaptopGpsMarkers,

  /**
   * Gets map markers for closest laptop gps point
   * @param {*} features
   */  
  getLaptopClosestGpsMarkers,
  
  /**
   * Gets map markers for laptop travel lines
   * @param {*} features
   * @param {*} fnDirectionalArrows
   */   
  getLaptopTravelLineMarkers,

  /**
   * Gets map markers for phone gps point
   * @param {*} features
   */   
  getPhoneGpsMarkers,

  /**
   * Gets map markers for closest phone gps points
   * @param {*} features
   */
  getPhoneClosestGpsMarkers,

  /**
   * Returns an array or map markers for phone travel lines
   * @param {*} features
   * @param {*} fnDirectionalArrows
   * @returns 
   */
  getPhoneTravelLineMarkers,

  getAssignmentMapMarkers,

  /** 
   * Generates content to be displayed in the cluster/stop point popup dialog
   * @param {*} data - geoJSON data
   */
  getClusterPopupContent,

  /** 
   * Generates content to be displayed in the laptop/phone gps popup dialog
   * @param {*} data - geoJSON data
   */
  getTrackPopupContent,

  getAssignmentStaffPopupContent,

  getAssignmentTaskPopupContent,

  /**
   * Gets map marker features for client events. Only markers for events with valid coordinates 
   * get created.
   * @param {*} events - client event data
   */
  getClientEventMarkers,

  /**
   * Generates content to be displayed in client event popup
   * @param {*} event 
  */
  getClientEventPopupContent
};

// #region Helpers

const MARKER_ICONS = MAP_CONSTANTS.MARKER_ICONS;
const ASSIGNMENT_MARKER_ICONS = MAP_CONSTANTS.CASE_ASSIGN_MAP_MARKER_ICONS;
let __renderer = null;

function isPointCompletedFeature (id, tasks) {
  let isPointComplete = false;
  for (let key in tasks) {
      if (id === tasks[key].PointCompleted) {
          isPointComplete = true;
          break;
      }
  }

  return isPointComplete;
}


// #endregion

function getUnsortedTasks (features) {
  if(!features) {
    return [];
  }

  return features.filter((feature) => {
    return (feature.properties.tag === TASK_CONSTANTS.TASK_TAGS.TASK || feature.properties.tag === TASK_CONSTANTS.TASK_TAGS.SUSPECT);
  })
  .map((feature) => {
    return feature.properties;
  });
}

function getAlternateAddresses (features) {
  let alt_address = [];

  if(!features) {
    return alt_address;
  }
  features.forEach((feature) => {
      let props = feature.properties;

      if ((feature.geometry === undefined || feature.geometry === null) &&
        (props.tag === TASK_CONSTANTS.TASK_TAGS.TASK && props.Status === TASK_CONSTANTS.TASK_STATUS.COMPLETED)) {
          props.NOLOC = "No Location";
      }

      if ((props.tag === TASK_CONSTANTS.TASK_TAGS.TASK || props.tag === TASK_CONSTANTS.TASK_TAGS.SUSPECT) &&
          (props.AlternateLocation === true && props.AltInterviewAddress && props.AltInterviewAddress.point)
      ) {
        let newfeature = JSON.parse(JSON.stringify(feature));
        newfeature.geometry.coordinates[0] = props.AltInterviewAddress.point.lon;
        newfeature.geometry.coordinates[1] = props.AltInterviewAddress.point.lat;
        newfeature.id = feature.id + "_alt";
        newfeature.properties.tag = TASK_CONSTANTS.TASK_TAGS.ALT_ADDRESS;
        alt_address.push(newfeature);
      }
  });

  return alt_address;
}

function sortTaskRelatedFeatures (features) {
  let homeAndTaskMarkers = {
      home: [],
      // altAddr: [],
      tasks: [],
      validTasks: [],
      validTasksAlternativeLocation: [],
      suspectTasks: [],
      erocTasks: [],
      mrocTasks: [],
      noDataTasks: [],
      polys: [],
      radius: [],
      other: [],
  };

  if(!features) {
    return homeAndTaskMarkers;
  }

  // Loop through the features and place each feature in 
  // it's corresponding array based on the tag field in 
  // the properties object
  features.forEach((feature) => {
      const props = feature.properties;
      //Staff home
      if (props.tag === TASK_CONSTANTS.TASK_TAGS.HOME) {
        homeAndTaskMarkers.home.push(feature);
      } else if(props.tag === TASK_CONSTANTS.TASK_TAGS.TASK && 
          (props.Status !== "VALID" && props.Status !== "COMPLETED" && props.Status !== "UNDER_REVIEW" && props.Status !== "CORRECTED")) { // Tasks           
          homeAndTaskMarkers.tasks.push(feature);
      } else if ((props.tag === TASK_CONSTANTS.TASK_TAGS.TASK || props.tag === TASK_CONSTANTS.TASK_TAGS.VALID_ALT) &&
          !(props.TaskType === TASK_CONSTANTS.TASK_TYPES.EROC || props.TaskType === TASK_CONSTANTS.TASK_TYPES.MROC) &&
          (props.Status === TASK_CONSTANTS.TASK_STATUS.VALID ||props.Status === TASK_CONSTANTS.TASK_STATUS.COMPLETED ||
              props.Status === TASK_CONSTANTS.TASK_STATUS.UNDER_REVIEW || props.Status === TASK_CONSTANTS.TASK_STATUS.CORRECTED)) {  // Valid tasks
          const alternateLocation = (
            props?.tag === TASK_CONSTANTS.TASK_TAGS.VALID_ALT ||
            props?.AlternateLocation === true ||
            props?.alternateLocation === true
          );
          if (alternateLocation) {
            homeAndTaskMarkers.validTasksAlternativeLocation.push(feature);
          } else {
            homeAndTaskMarkers.validTasks.push(feature);
          }
      } else if(props.tag === TASK_CONSTANTS.TASK_TAGS.SUSPECT || props.tag === TASK_CONSTANTS.TASK_TAGS.SUSPECT_ALT) { //Suspect tasks
          homeAndTaskMarkers.suspectTasks.push(feature);
      } else if(props.tag === TASK_CONSTANTS.TASK_TAGS.TASK && props.TaskType === TASK_CONSTANTS.TASK_TYPES.EROC) {   //EROC tasks
          homeAndTaskMarkers.erocTasks.push(feature);
      } else if(props.tag === TASK_CONSTANTS.TASK_TAGS.TASK && props.TaskType === TASK_CONSTANTS.TASK_TYPES.MROC) {   //MROC tasks
          homeAndTaskMarkers.mrocTasks.push(feature);
      } else if (props.tag === TASK_CONSTANTS.TASK_TAGS.POLY) {   //Poly
          homeAndTaskMarkers.polys.push(feature);
      } else if(props.tag === TASK_CONSTANTS.TASK_TAGS.NO_DATA || props.tag === TASK_CONSTANTS.TASK_TAGS.NO_DATA_ALT) {   //No data tasks
          homeAndTaskMarkers.noDataTasks.push(feature);
      } else if(props.tag === TASK_CONSTANTS.TASK_TAGS.OTHER) {   //Other such as unknown or under review
        homeAndTaskMarkers.other.push(feature);
      }
  });

  return homeAndTaskMarkers;
}

function sortClusterFeatures(features) {
  let clusterFeatures = {
    clusters: [],
    stopPoints: []
  };

  if(!features) {
    return clusterFeatures;
  }

  features.forEach((feature) => {
    //Cluster
    if (feature.properties.tag === TASK_CONSTANTS.TASK_TAGS.CLUSTER) {
      clusterFeatures.clusters.push(feature);
    } else {  // Stop point
      clusterFeatures.stopPoints.push(feature);
    }
  });

  return clusterFeatures;
}

function sortDayTrackMarkers(features, allTasks) {

  let dayTrackFeatures = {
      home: [],
      laptopTravelLines: [],
      phoneTravelLines: [],
      laptopGpsPoints: [],
      phoneGpsPoints: [],
      laptopClosestGpsPoint: [],
      phoneClosestGpsPoint: []
  };


  features.forEach((feature) => {
    const tag = feature.properties.tag;
    if (tag === TASK_CONSTANTS.TASK_TAGS.HOME) {
        dayTrackFeatures.home.push(feature);
        return
      }

    if(tag === TASK_CONSTANTS.TASK_TAGS.LAPTOP_TRAVEL_LINES || tag === TASK_CONSTANTS.TASK_TAGS.PHONE_TRAVEL_LINES) {
      if(tag === TASK_CONSTANTS.TASK_TAGS.LAPTOP_TRAVEL_LINES) {
        dayTrackFeatures.laptopTravelLines.push(feature);
      } else {
        dayTrackFeatures.phoneTravelLines.push(feature);
      }
    } else {
      const isPointCompleted = isPointCompletedFeature(feature.id, allTasks);
      if(!isPointCompleted) {   //not completed point means regular gps point
        // laptop gps 
        if (tag === TASK_CONSTANTS.TASK_TAGS.LAPTOP_TRACK) {
            dayTrackFeatures.laptopGpsPoints.push(feature);
        } else if (tag === TASK_CONSTANTS.TASK_TAGS.PHONE_TRACK) {
            dayTrackFeatures.phoneGpsPoints.push(feature);
        }
      } else {  //Completed 
        if (tag === TASK_CONSTANTS.TASK_TAGS.LAPTOP_TRACK || tag === TASK_CONSTANTS.TASK_TAGS.LAPTOP_TRACK_CLOSEST || tag === TASK_CONSTANTS.TASK_TAGS.LAPTOP_GPS_CLOSEST) {
            dayTrackFeatures.laptopClosestGpsPoint.push(feature);
        } else if (tag === TASK_CONSTANTS.TASK_TAGS.PHONE_TRACK || tag === TASK_CONSTANTS.TASK_TAGS.PHONE_TRACK_CLOSEST) {
            dayTrackFeatures.phoneClosestGpsPoint.push(feature);
        }
      }
    }
  });

  return dayTrackFeatures;
}

function getHomeAddressMarkers(features, fnPopup) {
  return L.geoJson(features, {
    onEachFeature: fnPopup,
    pointToLayer: function (feature, latlng) {
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(MARKER_ICONS.staffAddress),
        zIndexOffset: 998,
        title: "Interviewer Home",
        alt: "Home",
      });
    },
  });
};

function getNoDataTaskMarkers(features, fnPopup) {
  return L.geoJson(features, {    
    onEachFeature: fnPopup,
    pointToLayer: function (feature, latlng) {
      const props = feature.properties;
      const icon = (props.tag === TASK_CONSTANTS.TASK_TAGS.NO_DATA) ? MARKER_ICONS.noData : MARKER_ICONS.noDataAlt;
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(icon),
        zIndexOffset: 998,
        title: "No data for task - " + feature.properties.SPID,
        alt: "No Data",
      });
    },
  });
};

function getValidTaskMarkers(features, fnPopup) {
  console.log('getVALIDTaskMarkers features', features)
  return L.geoJson(features, {
    onEachFeature: fnPopup,
    filter: function (feature) {
      const props = feature.properties;
      const alternateLocation = (
        props?.tag === TASK_CONSTANTS.TASK_TAGS.VALID_ALT ||
        props?.AlternateLocation === true ||
        props?.alternateLocation === true
      );
      
      return !alternateLocation;
    },
    pointToLayer: function (feature, latlng) { 
      let validtask_title = "Valid Task ";
      let validtask_alt = "Valid";      
      const props = feature.properties;
      let validtask_icon = MARKER_ICONS.validtask;
      if (props.NOLOC && props.Status === TASK_CONSTANTS.TASK_STATUS.COMPLETED) {
        validtask_icon = MARKER_ICONS.nolocationtask;
      } else if (props.Status === TASK_CONSTANTS.TASK_STATUS.UNDER_REVIEW ) {
        validtask_icon = MARKER_ICONS.reviewtask;
      } else if (props.Status === TASK_CONSTANTS.TASK_STATUS.COMPLETED && props.StatusBasis === "E01_EXCLUDED_REMOTE") {
        validtask_icon = MARKER_ICONS.excludedtask;
        validtask_title = "Excluded Task ";
        validtask_alt = "Excluded";
      }
      const markerTitle = validtask_title + (feature.properties.SPID || "") + " " + (feature.properties.NOLOC || "");
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(validtask_icon),
        zIndexOffset: 999,
        title: markerTitle,
        alt: validtask_alt,
      });
    },
  });
};

function getValidTaskAlternativeLocationMarkers(features, fnPopup) {
  return L.geoJson(features, {
    onEachFeature: fnPopup,
    filter: function (feature) {
      const props = feature.properties;
      const alternateLocation = (
        props?.tag === TASK_CONSTANTS.TASK_TAGS.VALID_ALT ||
        props?.AlternateLocation === true ||
        props?.alternateLocation === true
      );
      return alternateLocation;
    },
    pointToLayer: function (feature, latlng) {    
      let validtask_title = "Valid Task ";
      let validtask_alt = "Valid";      
      const props = feature.properties;
      let validtask_icon = MARKER_ICONS.validAlt;
      if (props.NOLOC && props.Status === TASK_CONSTANTS.TASK_STATUS.COMPLETED) {
        validtask_icon = MARKER_ICONS.nolocationtask;
      } else if (props.Status === TASK_CONSTANTS.TASK_STATUS.UNDER_REVIEW ) {
        validtask_icon = MARKER_ICONS.reviewtask;
      } else if (props.Status === TASK_CONSTANTS.TASK_STATUS.COMPLETED && props.StatusBasis === "E01_EXCLUDED_REMOTE") {
        validtask_icon = MARKER_ICONS.excludedtask;
        validtask_title = "Excluded Task ";
        validtask_alt = "Excluded";
      }
      const markerTitle = validtask_title + (feature.properties.SPID || "") + " " + (feature.properties.NOLOC || "");
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(validtask_icon),
        zIndexOffset: 999,
        title: markerTitle,
        alt: validtask_alt,
      });
    },
  });
};

function getErocTaskMarkers(features, fnPopup) {
  return L.geoJson(features, {
    onEachFeature: fnPopup,
    pointToLayer: function (feature, latlng) {
      let task_icon = MARKER_ICONS.validtaskeroc;
      if (feature.properties.NOLOC && feature.properties.TaskType === TASK_CONSTANTS.TASK_TYPES.EROC) {
        task_icon = MARKER_ICONS.nolocationtask;
      } else if (feature.properties.TaskType === TASK_CONSTANTS.TASK_TYPES.EROC) {
        task_icon = MARKER_ICONS.validtaskeroc;
      }
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(task_icon),
        zIndexOffset: 998,
        title: "EROC Task - " + feature.properties.SPID + " " + (feature.properties.NOLOC || ""),
        alt: "Completed",
      });
    },
  });
};

function getMrocTaskMarkers(features, fnPopup) {
  return L.geoJson(features, {
    onEachFeature: fnPopup,
    pointToLayer: function (feature, latlng) {
      let task_icon = MARKER_ICONS.validtaskmroc;
      if (feature.properties.NOLOC && feature.properties.TaskType === TASK_CONSTANTS.TASK_TYPES.MROC) {
        task_icon = MARKER_ICONS.nolocationtask;
      } else if (feature.properties.TaskType === TASK_CONSTANTS.TASK_TYPES.MROC) {
        task_icon = MARKER_ICONS.validtaskmroc;
      }
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(task_icon),
        zIndexOffset: 998,
        title:"MROC Task - " + feature.properties.SPID + " " + (feature.properties.NOLOC || ""),
        alt: "Completed",
      });
    },
  });
};

function getTaskMarkers(features, fnPopup) {
  return L.geoJson(features, {
    onEachFeature: fnPopup,
    pointToLayer: function (feature, latlng) {
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(MARKER_ICONS.task),
        zIndexOffset: 998,
        title: "Task - " + feature.properties.SPID,
        alt: "Excluded",
      });
    },
  });
};

function getSuspectTaskMarkers(features, fnPopup) {
  return L.geoJson(features, {
    onEachFeature: fnPopup,
    pointToLayer: function (feature, latlng) {
      const props = feature.properties;
      const icon = (props.tag === TASK_CONSTANTS.TASK_TAGS.SUSPECT_ALT || (props?.AlternateLocation === true || props?.alternateLocation === true)) ? MARKER_ICONS.suspectAlt : MARKER_ICONS.suspecttask;
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(icon),
        zIndexOffset: 999,
        title: "Suspect Task - " + feature.properties.SPID,
        alt: "Suspect",
      });
    },
  });
};

function getPolyMarkers(features) {
  return L.geoJson(features, {
    style: function (feature) {
      return {
        fillColor: "#15667c", // blue
        color: "#15667c",
        weight: 2,
        opacity: 1,
        fillOpacity: 0.3,
      };
    },
  });
};

function getOtherMarkers(features, fnPopup) {
  return L.geoJson(features, {
    onEachFeature: fnPopup,
    pointToLayer: function (feature, latlng) {

      let img = MARKER_ICONS.noData;

      if (feature.properties.Status === "UNDER_REVIEW") {
        img = MARKER_ICONS.reviewtask;
      }

      const props = feature.properties;
      const icon = img;
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(icon),
        zIndexOffset: 998,
        title: "Other Task - " + feature.properties.SPID,
        alt: "Other",
      });
    },
  });
}

function getClusterMarkers(features, bLogIds) {
  
  if(bLogIds) {
    loggingService.logObjectToConsole(
      features, 
      {
        messageToLog: LOG_MESSAGES.LAPTOP_CLUSTER, 
        logMessageFirst: true,
        fieldToLog: 'id' 
      }
    );
  }

  return L.geoJson(features, {
    renderer: L.canvas(),
    pointToLayer: function (feature, latlng) {
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(MARKER_ICONS.cluster),
        riseOnHover: true,
        title: "Cluster - " + feature.id,
        alt: "Cluster",
        zIndexOffset: 1001
      });
    },
  });
}; 

function getStopPointMarkers(features, bLogIds) {

  if(bLogIds) {
    loggingService.logObjectToConsole(
      features, 
      {
        messageToLog: LOG_MESSAGES.PHONE_CLUSTER, 
        logMessageFirst: true,
        fieldToLog: 'id' 
      }
    );
  }

  return L.geoJson(features, {
      renderer: L.canvas(),
      pointToLayer: function (feature, latlng) {
        return L.marker(latlng, {
          renderer: L.canvas(),
          icon: L.icon(MARKER_ICONS.stop),
          riseOnHover: true,
          title: "Phone Stop - " + feature.id,
          alt: "Phone Stop",
          zIndexOffset: 1001
        });
      },
    });
};

function getLaptopGpsMarkers(features) { 

  return L.geoJson(features, {
    renderer: L.canvas(),
    pointToLayer: function (feature, latlng) {

      // We default to SATELLITE
      let icon = MARKER_ICONS.gpsLaptop;

      const satHdop = 
      console.log('SAT HDOP: ', feature?.properties?.satHdop);

      if(feature?.properties?.locationSource === 'CELLULAR') {
        icon = MARKER_ICONS.gpsLaptopCell;
      } else if (feature?.properties?.locationSource === 'WIFI') {
        icon = MARKER_ICONS.gpsLaptopWifi;
      } else if(feature?.properties?.locationSource === 'IPADDRESS') {
        icon = MARKER_ICONS.gpsLaptopIp;
      } else {
        icon = MARKER_ICONS.gpsLaptop;
        const satHdop = feature?.properties?.satHdop || 0;
        if(satHdop >= 500) {
          icon = MARKER_ICONS.gpsLaptopBadHdop;
        }        
      }

      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(icon),
        riseOnHover: true,
        title: "GPS Point from laptop",
        alt: "Laptop",
        zIndexOffset: 1000
      });
    },
  });  
};

function getLaptopClosestGpsMarkers(features, bLogIds) {

  if(bLogIds) {
    loggingService.logObjectToConsole(
      features, 
      {
        messageToLog: LOG_MESSAGES.LAPTOP_CLOSEST_POINT, 
        logMessageFirst: true,
        fieldToLog: 'id' 
      }
    );
  }

  return L.geoJson(features, {
    renderer: L.canvas(),
    pointToLayer: function (feature, latlng) {
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(MARKER_ICONS.gpsLaptopclosest),
        riseOnHover: true,
        title: "Closest Laptop GPS Point - " + feature.id,
        alt: "Closest Laptop",
        zIndexOffset: 1001
      });
    },
  });
};

function getLaptopTravelLineMarkers(features, fnDirectionalArrows, bLogIds) {

  if(bLogIds) {
    loggingService.logObjectToConsole(
      features, 
      {
        messageToLog: LOG_MESSAGES.LAPTOP_TRAVEL_LINES, 
        logMessageFirst: true,
        fieldToLog: 'id' 
      }
    );
  }

  // lines with direction
  return L.geoJson(features, {
    renderer: L.canvas(),
    onEachFeature: fnDirectionalArrows,
    style: function (feature) {
      return {
        color: "#0089f7",
        weight: 2,
        opacity: 1,
        zIndex: 1
      };
    }
  });
};

function getPhoneGpsMarkers(features) {
  // GPS points from smartphone
  return L.geoJson(features, {
    renderer: L.canvas(),
    pointToLayer: function (feature, latlng) {
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(MARKER_ICONS.gpsPhone),
        riseOnHover: true,
        title: "GPS Point from phone",
        alt: "Phone",
        zIndexOffset: 1000
      })
    },
  });  
};

function getPhoneClosestGpsMarkers(features, bLogIds) {

  if(bLogIds) {
    loggingService.logObjectToConsole(
      features, 
      {
        messageToLog: LOG_MESSAGES.PHONE_CLOSEST_POINT, 
        logMessageFirst: true,
        fieldToLog: 'id'
      }
    );
  }

  return L.geoJson(features, {
    renderer: L.canvas(),
    pointToLayer: function (feature, latlng) {
      return L.marker(latlng, {
        renderer: L.canvas(),
        icon: L.icon(MARKER_ICONS.gpsPhoneclosest),
        riseOnHover: true,
        title: "Closest Phone GPS Point - " + feature.id,
        alt: "Closest Phone",
        zIndexOffset: 1001
      })
    },
  });  
};

function getPhoneTravelLineMarkers(features, fnDirectionalArrows, bLogIds) {

  if(bLogIds) {
    loggingService.logObjectToConsole(
      features, 
      {
        messageToLog: LOG_MESSAGES.PHONE_TRAVEL_LINES, 
        logMessageFirst: true,
        fieldToLog: 'id'
      }
    );  
  }

  // travel lines from phone
  return L.geoJson(features, {
    renderer: L.canvas(),
    onEachFeature: fnDirectionalArrows,
    style: function (feature) {
      return {
        color: "#e77504",
        weight: 2,
        opacity: 1,
        zIndex: 1
      };
    },
  });
};

function getClusterPopupContent(data) {

  let template = '',
    staffId = (data.StaffId) ? data.StaffId : 'No Data',
    staffName = (data.StaffName) ? data.StaffName : 'No Data',
    startTime = (data.DateStart) ? data.DateStart : 'No Data',
    endTime = (data.DateEnd) ? data.DateEnd : 'No Data',
    points = (data.Points) ? data.Points : 'No Data',
    clusterId = (data.ClusterId) ? data.ClusterId : 'No Data',
    duration = (data.ClusterDuration) ? data.ClusterDuration : 'No Data';
    //Duration
    if(duration) {
      let parts = duration.split(' ');
      duration = parts[0] + 'h ' + parts[1] + 'm ' + parts[2] + 's';
    }

  if(data.tag == "cluster") {
    
    template = '<table class="popup-table">' +
    '<tbody>' +
      '<tr class="even">' +
          '<th> Staff ID</th>' +
          '<td>' + staffId + '</td>' +
      '</tr>' +
      '<tr class="odd">' +
          '<th> Staff Name</th>' +
          '<td>' + staffName + '</td>' +
      '</tr>' +
      '<tr class="even">' +
          '<th> Duration</th>' +
          '<td>' + duration + '</td>' +
      '</tr>' +
      '<tr class="odd">' +
          '<th> Start Time</th>' +
          '<td>' + startTime + '</td>' +
      '</tr>' +
      '<tr class="even">' +
          '<th> End Time</th>' +
          '<td>' + endTime + '</td>' +
      '</tr>' +
      '<tr class="odd">' +
          '<th> GPS Points</th>' +
          '<td>' + points + '</td>' +
      '</tr>' +
      '<tr class="odd">' +
          '<th> Cluster Id</th>' +
          '<td>' + clusterId + '</td>' +
      '</tr>' +
      '</tbody>' +
    '</table>';    
  } else {
    template = '<table class="popup-table">' +
        '<tbody>' +
        '<tr class="even">' +
            '<td> Staff ID</td>' +
            '<td>' + staffId + '</td>' +
        '</tr>' +
        '<tr class="odd">' +
            '<td> Staff Name</td>' +
            '<td>' + staffName + '</td>' +
        '</tr>' +
        '<tr class="even">' +
            '<td> Duration</td>' +
            '<td>' + duration + '</td>' +
        '</tr>' +
        '<tr class="odd">' +
            '<td> Start Time</td>' +
            '<td>' + startTime + '</td>' +
        '</tr>' +
        '<tr class="even">' +
            '<td> End Time</td>' +
            '<td>' + endTime + '</td>' +
        '</tr>' +
        '<tr class="odd">' +
            '<td> GPS Points</td>' +
            '<td>' + points + '</td>' +
        '</tr>' +
        '</tbody>' +
    '</table>';
  }
  return template;
};

function getTrackPopupContent(data) {
  let Source = (data.Source) ? data.Source : 'No Data',
    Time = (data.Time) ? data.Time : 'No Data',
    Speed = (!data.Speed && data.Speed !== 0) ?  'No Data' : data.Speed + ' MPH',
    Device = (data.Device) ? data.Device : 'No Data',
    Heading = (data.Heading) ? data.Heading : 'No Data',
    Lat = (data.Lat) ? data.Lat : 'No Data',
    Lon = (data.Lon) ? data.Lon : 'No Data',
    Satellites = (data.Satellites) ? data.Satellites : 'No Data',
    HDOP = (data.HDOP) ? data.HDOP : 'No Data',
    locationSource = (data.locationSource) ? data.locationSource : 'No Data',
    satHdop = (data.satHdop) ? data.satHdop : 'No Data';
    // let StaffAddress = (data.staffAddress) ? data.staffAddress : 'No Data';

  return '<div class="map-popup-actionbar">' +
    '<table class="popup-table">' +
    '<tbody>' +
        '<tr class="even">' +
            '<td>Source</td>' +
            '<td>' + Source + '</td>' +
        '</tr>' +
        '<tr class="odd">' +
            '<td>Date</td>' +
            '<td>' + Time + '</td>' +
        '</tr>' +
        '<tr class="even">' +
            '<td>Speed</td>' +
            '<td>' + Speed + '</td>' +
        '</tr>' +
        '<tr class="odd">' +
            '<td>Device</td>' +
            '<td>' + Device + '</td>' +
        '</tr>' +
        '<tr class="even">' +
            '<td>Heading</td>' +
            '<td>' + Heading + '</td>' +
        '</tr>' +
        '<tr class="odd">' +
            '<td>Lat</td>' +
            '<td data-cy="map-popup-point-lat">' + Lat + '</td>' +
        '</tr>' +
        '<tr class="even">' +
            '<td>Lon</td>' +
            '<td data-cy="map-popup-point-lon">' + Lon + '</td>' +
        '</tr>' +
        '<tr class="odd">' +
            '<td>Satellites</td>' +
            '<td>' + Satellites + '</td>' +
        '</tr>' +
        '<tr class="even">' +
            '<td>HDOP</td>' +
            '<td>' + HDOP + '</td>' +
        '</tr>' +
        '<tr class="even">' +
            '<td>Location Source:</td>' +
            '<td>' + locationSource + '</td>' +
        '</tr>' +
        '<tr class="even">' +
            '<td>Satellite HDOP</td>' +
            '<td>' + satHdop + '</td>' +
        '</tr>' +
        // '<tr class="even">' +
        //     '<td>Staff Address</td>' +
        //     '<td>' + StaffAddress + '</td>' +
        // '</tr>' +
      '</tbody>' +              
    '</table>' +
  '</div>';
};

function sortAssignmentMarkers(features) {
  let sortedFeatures = {
    unknown: [],
    staff: [],
    assignedArea: [],
    unassigned: [],
    unassignedHighPriority: [],
    notReviewed: [],
    notReviewedHighPriority: [],
    confirmed: [],
    confirmedHighPriority: [],
    assigned: [],
    assignedHighPriority: [],
    screenerUnassigned: [],
    screenerUnassignedHighPriority: [],
    screenerNotReviewed: [],
    screenerNotReviewedHighPriority: [],
    screenerConfirmed: [],
    screenerConfirmedHighPriority: [],
    screenerAssigned: [],
    screenerAssignedHighPriority: [],
    allAssignedTasks: {}
  };


  features.forEach((feature) => {
    switch(feature.properties.tag) {
      case 'assignedArea':
        sortedFeatures.assignedArea.push(feature);
        break;
      case '11_10_0': //unassigned
        sortedFeatures.unassigned.push(feature);
        break;
      case '11_20_0': //not reviewed
        sortedFeatures.notReviewed.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'notReviewed');
        break;
      case '11_30_0': //confirmed
        sortedFeatures.confirmed.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'confirmed');
        break;
      case '11_40_0': //assigned
        sortedFeatures.assigned.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'assigned');
        break;
      case '11_10_1': //unassigned hp
        sortedFeatures.unassignedHighPriority.push(feature);
        break;
      case '11_20_1': // not reviewed hp
        sortedFeatures.notReviewedHighPriority.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'notReviewedHighPriority');
        break;
      case '11_30_1': //confirmed hp
        sortedFeatures.confirmedHighPriority.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'confirmedHighPriority');
        break;
      case '11_40_1': //unassigned hp
        sortedFeatures.assignedHighPriority.push(feature);
        break;
      case '11.1_10_0': //screener unassigned
        sortedFeatures.screenerUnassigned.push(feature);
        break;
      case '11.1_20_0': //screener not reviewed
        sortedFeatures.screenerNotReviewed.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'screenerNotReviewed');
        break;
      case '11.1_30_0': //screener confirmed
        sortedFeatures.screenerConfirmed.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'screenerConfirmed');
        break;
      case '11.1_40_0': //screener assigned 
        sortedFeatures.screenerAssigned.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'screenerAssigned');
        break;
      case '11.1_10_1': //screener unassigned hp
        sortedFeatures.screenerUnassignedHighPriority.push(feature);
        break;
      case '11.1_20_1': //screener not reviewed hp
        sortedFeatures.screenerNotReviewedHighPriority.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'screenerNotReviewedHighPriority');
        break;
      case '11.1_30_1': //screener confirmed hp
        sortedFeatures.screenerConfirmedHighPriority.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'screenerConfirmedHighPriority');
        break;
      case '11.1_40_1': //screener assigned hp
        sortedFeatures.screenerAssignedHighPriority.push(feature);
        addAssignedStaffTask(sortedFeatures, feature, 'screenerAssignedHighPriority');
        break;
      case 'staff':  //staff
        sortedFeatures.staff.push(feature);
        break;
      default:
        sortedFeatures.unknown.push(feature);
        break;
    }
  });

  return sortedFeatures;
};

function getAssignmentMapMarkers(features) {  
    return L.geoJSON(features, {
      renderer: L.canvas(),
      pointToLayer: function (feature, latlng) {
        if (feature.properties.tag) {
          let fileName = (ASSIGNMENT_MARKER_ICONS[feature.properties.tag].icon) ? 
            String(ASSIGNMENT_MARKER_ICONS[feature.properties.tag].icon) :
            String(ASSIGNMENT_MARKER_ICONS['11_10_0'].icon);
          return L.marker(latlng, {
            renderer: L.canvas(),
            icon: new L.Icon({
              iconUrl: fileName,
              iconSize: [21, 21], // halved the icon size
            }),
          });
        } else {  //DD - not sure this is correct, these seem malformed....
          // console.log("No tag for CaseType:" + feature.properties['Case Type']);
          if (feature.properties['Case Type'].toLowerCase() === 'sc') {
            let fileName = (feature.properties.Priority === 0) ? 
              String(ASSIGNMENT_MARKER_ICONS['11.1_10_0'].icon) : 
              String(ASSIGNMENT_MARKER_ICONS['11.1_10_1'].icon);
            return L.marker(latlng, {
              renderer: L.canvas(),
              icon: new L.Icon({
                iconUrl: fileName,
                iconSize: [21, 21], // halved the icon size
              }),
            });
          }                    
        }
      },
    });
};

function getAssignmentStaffPopupContent(info) {
  return `<table>
  <tbody>
  <tr><td>STAFF ID: </td><td>
    ${info.staffId}
  </td></tr>
  <tr><td>STAFF NAME: </td><td>
    ${info.staffName}
  </td></tr>
  <tr><td>CASES: </td><td>
    ${info.count}
  </td></tr>
  <tr><td>UTILIZATION: </td><td>
    ${info.utilization}
  </td></tr>
  <tr><td>TRAVEL TIME: </td><td>
    ${info.durationTravel}
  </td></tr>
  <tr><td>TASK TIME: </td><td>
    ${info.durationTask}
  </td></tr>
  <tr>
    <button class="unassign-button btn btn-outline-danger btn-sm ml-auto">
        Unassign All Cases
    </button>
    </tr>
    </tbody>
  </table>`;
};

function getAssignmentTaskPopupContent(status, popupAddress, properties) {
  return `<table>
  <tbody>
  <tr><td>ADDRESS: </td><td>
    ${popupAddress}
  </td></tr>
  <tr><td>StaffId: </td><td>
    ${properties.StaffId}
  </td></tr>
  <tr><td>DUID: </td><td>
    ${properties.DUID}
  </td></tr>
  <tr><td>PSU: </td><td>
    ${properties.PSU}
  </td></tr>
  <tr><td>STATUS: </td><td>
  <button id="status-${
    properties.DUID}" class="status-button btn btn-outline-danger btn-sm ml-auto" onclick="let testEvent = new CustomEvent(\'status\', { bubbles: true, detail: \'${
properties.DUID
}\'}); document.getElementById(\'status-${
properties.DUID
}\').dispatchEvent(testEvent);">
    ${status}
  </button>
  </td></tr>
  <tr><td>NOTES: </td><td>
  <button id="comments-${
    properties.DUID
  }" class="notes-button ${
properties.DUID
} btn btn-outline-danger btn-sm ml-auto" onclick="let testEvent = new CustomEvent(\'comments\', { bubbles: true, detail: \'${
properties.DUID
}\'}); document.getElementById(\'comments-${
properties.DUID
}\').dispatchEvent(testEvent);">
    <img src= ${ MAP_CONSTANTS.ASSIGNMENT_POPUP_TASK_IMG } width="30%" height="30%">
  </button>
  </td></tr>
  <tr>
  </tr>
  </tbody>
  </table>`;
};

function addAssignedStaffTask(sortedFeatures, feature, layerName) {
  let staffTasks = sortedFeatures.allAssignedTasks[feature.properties.StaffId];
  if(!staffTasks) {
    staffTasks = getStaffAssignedTasks();
    sortedFeatures.allAssignedTasks[feature.properties.StaffId] = staffTasks;
  }
  staffTasks[layerName].push(feature);
}

function getStaffAssignedTasks () {
  return Object.create({
		notReviewed: [],
		notReviewedHighPriority: [],
		confirmed: [],
		confirmedHighPriority: [],
		assigned: [],
		assignedHighPriority: [],
		screenerNotReviewed: [],
		screenerNotReviewedHighPriority: [],
		screenerConfirmed: [],
		screenerConfirmedHighPriority: [],
		screenerAssigned: [],
		screenerAssignedHighPriority: []
	});
}

function getClientEventMarkers(events) {
  let features = [];
  for(let event of events) {
    if(event.point.lon != 0 && event.point.lat != 0) {
      features.push({
        type: "Feature",
        properties: event,
        geometry: {
            type: "Point",
            coordinates: [event.point.lon, event.point.lat]
        }
      });
    }      
  }

  const eventTypeIcons = {
    DEVICE: L.icon(MARKER_ICONS.deviceEvent),
    LOCATION_SERVICE: L.icon(MARKER_ICONS.locationServiceEvent),
    NETWORKING: L.icon(MARKER_ICONS.networkingEvent),
    POWER: L.icon(MARKER_ICONS.powerEvent),
    DEFAULT: L.icon(MARKER_ICONS.clientEvent)
  };

   return L.geoJson(features, {
    pointToLayer: function (feature, latlng) {
  const { eventType, severity = [] } = feature.properties;

  const isFatal = severity.includes("FATAL");

  const icon = isFatal
    ? L.icon(MARKER_ICONS.fatalEvent)
    : (eventTypeIcons[eventType] || eventTypeIcons.DEFAULT);

  const zIndex = isFatal ? 5000 : 998;

  return L.marker(latlng, {
    renderer: L.canvas(),
    icon: icon,
    zIndexOffset: zIndex,
    title: `${eventType} Event`,
    alt: `${eventType} Event`,
  });
}
  });
};


function getClientEventPopupContent(event) {

  const deviceId = (event.deviceId) ? event.deviceId : 'No Data';
  const eventType = (event.eventType) ? event.eventType : 'No Data';
  const message = (event.message) ? event.message : 'No Data';
  const severity = (event.severity) ? event.severity : 'No Data';
  const timestamp = (event.timestamp) ? dateService.formatDateTime(event.timestamp) : 'No Data';
  const staffAddress = event.staffAddress || 'No Data';

  let template = '<div class="popup-content">' +
      '<table class="popup-table">' +
      '<tbody>' +
          '<tr class="even">' +
              '<td> Device ID</td>' +
              '<td>' + deviceId + '</td>' +
          '</tr>' +
          '<tr class="odd">' +
              '<td> Event Type</td>' +
              '<td>' + eventType + '</td>' +
          '</tr>' +
          '<tr class="even">' +
              '<td> Event Message</td>' +
              '<td>' + message + '</td>' +
          '</tr>' +
          '<tr class="odd">' +
              '<td> Severity</td>' +
              '<td>' + severity + '</td>' +
          '</tr>' +
          '<tr class="even">' +
              '<td> Time Captured</td>' +
              '<td>' + timestamp + '</td>' +
          '</tr>' +
          '<tr class="odd">' +
              '<td>Staff Address</td>' +
              '<td>' + staffAddress + '</td>' +
          '</tr>' +
          '</tbody>' +
      '</table>' + 
    '</div>';
        
    return template;
}

const LOG_MESSAGES = {
  LAPTOP_CLUSTER: 'Cluster IDs: ',
  LAPTOP_CLOSEST_POINT: 'Closest Laptop GPS Points IDs: ',
  LAPTOP_TRAVEL_LINES: 'Laptop Travel Line IDs: ',
  PHONE_CLUSTER: 'Phone Stop IDs: ',
  PHONE_CLOSEST_POINT: 'Closest Phone GPS Points IDs: ',
  PHONE_TRAVEL_LINES: 'Phone Travel Line IDs: '
};